import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { post } from "@rails/request.js"
import jquery from "jquery"

const $ = jquery

export default class extends Controller {
  static targets = ["trigger", "inputWrapper", "returnTo", "select", "selectShell", "submit", "submitIcon", "submitSpinner", "pickerFrame"]

  static values = {
    url: String,
    model: String,
    field: { type: String, default: "job_id" },
    labelField: { type: String, default: "" },
    fallbackUrl: String,
    modalParam: String,
    existingLabels: { type: Array, default: [] },
    confirmations: { type: Object, default: {} },
    repeatable: { type: Boolean, default: false },
    fullWidthWhenExpanded: { type: Boolean, default: false },
    clearExpandedOnSubmit: { type: Boolean, default: false },
    forceDropdownBelow: { type: Boolean, default: false },
    pickerUrl: String,
    frameId: String,
    deferred: { type: Boolean, default: false },
    creating: { type: Boolean, default: false }
  }

  connect() {
    this.connected = true
    if (this.deferredValue && !this.shouldAutoExpand()) {
      this.controllerActive = false
      return
    }

    this.activateController()
  }

  activateController() {
    if (this.controllerActive) return
    this.controllerActive = true

    this.handleTurboRender = this.handleTurboRender?.bind(this) || this.onTurboRender.bind(this)
    this.handleKeydown = this.handleKeydown?.bind(this) || this.onKeydown.bind(this)
    this.handlePointerDown = this.handlePointerDown?.bind(this) || this.onPointerDown.bind(this)
    this.handleOpenRequest = this.handleOpenRequest?.bind(this) || this.onOpenRequest.bind(this)
    this.handleSelectOpen = this.handleSelectOpen?.bind(this) || this.onSelectOpen.bind(this)
    this.handleSelectClose = this.handleSelectClose?.bind(this) || this.onSelectClose.bind(this)
    this.handleSelectChange = this.handleSelectChange?.bind(this) || this.onSelectChange.bind(this)
    this.handleSearchInput = this.handleSearchInput?.bind(this) || this.onSearchInput.bind(this)
    this.handleFormSubmit = this.handleFormSubmit?.bind(this) || this.onFormSubmit.bind(this)
    this.handleTurboSubmitEnd = this.handleTurboSubmitEnd?.bind(this) || this.onTurboSubmitEnd.bind(this)

    document.addEventListener("turbo:render", this.handleTurboRender)
    document.addEventListener("turbo:load", this.handleTurboRender)
    document.addEventListener("keydown", this.handleKeydown, true)
    document.addEventListener("input", this.handleSearchInput, true)
    document.addEventListener("keyup", this.handleSearchInput, true)
    document.addEventListener("pointerdown", this.handlePointerDown)
    document.addEventListener("inline-select-create:open", this.handleOpenRequest)
    this.element.addEventListener("$select2:open", this.handleSelectOpen)
    this.element.addEventListener("$select2:close", this.handleSelectClose)
    this.bindPickerMarkup()

    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  disconnect() {
    this.connected = false
    if (!this.controllerActive) return

    document.removeEventListener("turbo:render", this.handleTurboRender)
    document.removeEventListener("turbo:load", this.handleTurboRender)
    document.removeEventListener("keydown", this.handleKeydown, true)
    document.removeEventListener("input", this.handleSearchInput, true)
    document.removeEventListener("keyup", this.handleSearchInput, true)
    document.removeEventListener("pointerdown", this.handlePointerDown)
    document.removeEventListener("inline-select-create:open", this.handleOpenRequest)
    this.element.removeEventListener("$select2:open", this.handleSelectOpen)
    this.element.removeEventListener("$select2:close", this.handleSelectClose)
    this.formElement?.removeEventListener("submit", this.handleFormSubmit)
    this.formElement?.removeEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    this.unbindSelectChange()
    this.visibilityObserver?.disconnect()
    this.repeatableInsertionObserver?.disconnect()
    this.controllerActive = false
  }

  deferredValueChanged() {
    if (!this.connected || this.deferredValue || this.controllerActive) return

    this.activateController()
  }

  inputWrapperTargetConnected() {
    if (this.deferredValue && !this.shouldAutoExpand()) return

    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  triggerTargetConnected() {
    if (this.deferredValue && !this.shouldAutoExpand()) return

    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  // Everything here depends on the picker's form markup. The canvas no longer
  // ships that markup -- it arrives when the picker is opened -- so this runs
  // both at connect (for surfaces that still render the form inline, e.g. the
  // modal) and again when the fetched picker lands. Re-binding is safe: the
  // listeners are bound instance properties, so addEventListener dedupes them.
  bindPickerMarkup() {
    this.formElement?.addEventListener("submit", this.handleFormSubmit)
    this.formElement?.addEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    this.bindSelectChange()
    this.setCreatingState(this.creatingValue)
    this.syncReturnToField()
  }

  selectTargetConnected() {
    // The picker is fetched on open, so its select arrives after the click.
    // Stimulus registers the target once the frame's markup lands, which is
    // the first moment the expansion can actually be completed -- doing it on
    // turbo:frame-load is too early, the target is not connected yet.
    if (this.awaitingPicker) {
      this.awaitingPicker = false
      this.bindPickerMarkup()
      this.renderExpandedState()
      document.dispatchEvent(new CustomEvent("canvas-activity:complete", {bubbles: true}))
      document.dispatchEvent(new CustomEvent("canvas-activity:visible", {bubbles: true}))
      document.dispatchEvent(new CustomEvent("inline-select-create:open", {detail: {id: this.element.id}}))
      return
    }

    if (this.deferredValue && !this.shouldAutoExpand()) return

    this.bindSelectChange()
    this.restoreExpandedState()
  }

  selectTargetDisconnected(element) {
    // Reset before the selectChangeElement guard: the select that vanished is
    // not necessarily the one change-binding tracked, and the stuck state this
    // prevents does not care which it was.
    //
    // A canvas re-render (CableReady updates the slot's region after a create)
    // can put the empty frame back while this controller element survives, so
    // no reconnect happens and pickerLoaded still claims the markup is there.
    // The next open would then expand onto an empty frame with its trigger
    // already hidden. Only reset when the frame really is empty -- select2
    // teardown disconnects the target transiently while the markup stays (#223).
    const frame = this.hasPickerFrameTarget
      ? this.pickerFrameTarget
      : (this.hasFrameIdValue ? document.getElementById(this.frameIdValue) : null)
    if (frame && !frame.querySelector("select")) {
      this.pickerLoaded = false
      this.awaitingPicker = false
    }

    if (this.selectChangeElement !== element) return
    this.unbindSelectChange()
  }

  shouldAutoExpand() {
    if (!this.element.id) return false
    const url = new URL(window.location.href)
    return url.searchParams.get("expand") === this.element.id
  }

  setExpandedUrl(active) {
    if (!this.element.id) return

    const url = new URL(window.location.href)
    if (active) {
      url.searchParams.set("expand", this.element.id)
    } else {
      url.searchParams.delete("expand")
    }
    window.history.replaceState({}, "", url.toString())
    this.syncReturnToField()
  }

  renderExpandedState() {
    this.autoOpenCancelled = false
    this.element.classList.toggle("basis-full", this.repeatableValue || this.fullWidthWhenExpandedValue)

    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.add("hidden")
      this.triggerTarget.hidden = true
    }
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.remove("hidden")
      this.inputWrapperTarget.hidden = false
      this.prepareSelectForEditing()
    }
  }

  expandedStateDrifted() {
    if (!this.hasInputWrapperTarget) return false

    const shouldBeExpanded = this.shouldAutoExpand()
    const wrapperHidden = this.inputWrapperTarget.classList.contains("hidden")
    const triggerVisible = this.hasTriggerTarget && !this.triggerTarget.classList.contains("hidden")
    const triggerDrifted = this.hasTriggerTarget && (shouldBeExpanded ? triggerVisible : !triggerVisible)

    // An expanded control whose picker markup never arrived has drifted too,
    // even though its shell looks right. The server renders that shell from
    // ?expand= -- wrapper open, trigger hidden -- but the frame's contents are
    // this controller's job now, so shell-only comparison saw no drift and the
    // control sat there expanded, empty, and with nothing left to click (#223).
    if (shouldBeExpanded && this.hasPickerUrlValue && this.pickerUrlValue && !this.hasSelectTarget) {
      return true
    }

    return (shouldBeExpanded ? wrapperHidden : !wrapperHidden) || triggerDrifted
  }

  scheduleRestore() {
    if (this.restoreScheduled) return
    this.restoreScheduled = true

    setTimeout(() => {
      this.restoreScheduled = false
      this.restoreExpandedState()
    }, 0)
  }

  restoreExpandedState() {
    if (this.shouldAutoExpand()) {
      // A deep link (?expand=<id>) opens this picker without anyone touching
      // the trigger, so the fetch showInput() would have started has to happen
      // here too. Without it the disclosure opens onto an empty frame -- the
      // one path where deferring the markup changes behaviour rather than just
      // timing (#223).
      if (this.hasPickerUrlValue && this.pickerUrlValue && !this.pickerLoaded && !this.awaitingPicker) {
        this.loadPicker()
        return
      }
      this.renderExpandedState()
    } else {
      this.renderCollapsedState()
    }
  }

  showInput(event) {
    event?.preventDefault()

    // The picker's markup is not on the page: the canvas never builds it,
    // because every candidate's conflict preview walks that person's
    // assignments across the whole space. Load this one job's picker into its
    // frame instead of re-rendering the entire canvas (#223).
    if (this.hasPickerUrlValue && this.pickerUrlValue && !this.pickerLoaded && !this.awaitingPicker) {
      this.loadPicker(event)
      return
    }

    this.setExpandedUrl(true)
    this.renderExpandedState()
    document.dispatchEvent(new CustomEvent("inline-select-create:open", { detail: { id: this.element.id } }))
  }

  // Fetches this assigned job's picker into its turbo-frame, then expands once
  // the frame has rendered. Failure leaves the trigger usable and falls back to
  // the full form at fallbackUrl.
  loadPicker(event) {
    // Idempotent while a fetch is in flight. onTurboRender deliberately retries
    // restoreExpandedState on four timers, and without this guard each one
    // reassigns frame.src -- four requests per render, and a render triggered
    // by the response loops the canvas into hammering the server.
    if (this.awaitingPicker) return

    const frame = this.hasPickerFrameTarget ? this.pickerFrameTarget : document.getElementById(this.frameIdValue)
    if (!frame) {
      window.location.href = this.fallbackUrlValue
      return
    }

    document.dispatchEvent(new CustomEvent("canvas-activity:start", {
      bubbles: true,
      detail: {
        action: "open_person_picker",
        control: this.hasTriggerTarget ? this.triggerTarget : event?.currentTarget
      }
    }))

    // Reveal the wrapper BEFORE the fetch: the frame renders into a visible
    // container, the user gets immediate feedback, and the select shell is
    // present as soon as its markup arrives rather than one paint later.
    this.setExpandedUrl(true)
    this.autoOpenCancelled = false
    this.element.classList.toggle("basis-full", this.repeatableValue || this.fullWidthWhenExpandedValue)
    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.add("hidden")
      this.triggerTarget.hidden = true
    }
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.remove("hidden")
      this.inputWrapperTarget.hidden = false
    }

    const onLoad = () => {
      frame.removeEventListener("turbo:frame-load", onLoad)
      this.pickerLoaded = true
      // Values the picker needs travel with its markup, since they come from
      // the same preview computation. They sit on a child element because
      // Turbo replaces a frame's children, not its attributes.
      const values = frame.querySelector("[data-picker-values]")
      const labels = values?.getAttribute("data-existing-labels")
      const confirmations = values?.getAttribute("data-confirmations")
      if (labels) this.existingLabelsValue = JSON.parse(labels)
      if (confirmations) this.confirmationsValue = JSON.parse(confirmations)
      // selectTargetConnected finishes the expansion once Stimulus has
      // registered the select that just arrived.
    }
    this.awaitingPicker = true
    frame.addEventListener("turbo:frame-load", onLoad)
    // Release the in-flight guard if the frame never loads, so a failed fetch
    // leaves the picker retryable rather than permanently stuck closed.
    clearTimeout(this.pickerTimeout)
    this.pickerTimeout = setTimeout(() => {
      if (!this.awaitingPicker) return
      this.awaitingPicker = false
      frame.removeEventListener("turbo:frame-load", onLoad)
    }, 10000)
    // Same phase vocabulary the submit flow uses, so opening a picker stays
    // measurable: init = request dispatched, complete/visible = usable.
    document.dispatchEvent(new CustomEvent("canvas-activity:init", {bubbles: true}))
    frame.src = this.pickerUrlValue
  }

  hideInput() {
    this.autoOpenCancelled = true
    this.closeSelect()
    this.clearSelection()
    this.renderCollapsedState()
  }

  renderCollapsedState() {
    this.autoOpenCancelled = true
    this.layoutAnchorTop = null
    this.element.classList.remove("basis-full")
    this.clearSelection()
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.add("hidden")
      this.inputWrapperTarget.hidden = true
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.remove("hidden")
      this.triggerTarget.hidden = false
    }
  }

  cancel() {
    this.setExpandedUrl(false)
    this.hideInput()
    this.dispatch("cancelled")
  }

  exitToModal(event) {
    event?.preventDefault()

    const value = this.pendingModalValue()
    this.setExpandedUrl(false)
    this.openModal(value)
  }

  onKeydown(event) {
    if (!this.isExpanded()) return
    if (!this.isWithinOwnedControl(event.target)) return

    if (event.key === "Enter") {
      this.handleEnter(event)
      return
    }

    if (event.key === "Escape") {
      this.handleEscape(event)
    }
  }

  handleEnter(event) {
    if (!this.isWithinSelectControl(event.target)) return

    if (this.repeatableValue) {
      this.handleRepeatableEnter(event)
      return
    }

    if (this.isSelectOpen()) {
      const searchTerm = this.searchTermFor(event.target)
      if (this.isDuplicateCandidate(searchTerm)) {
        event.preventDefault()
        event.stopPropagation()
        this.syncDuplicateSearchFeedback(searchTerm)
        return
      }

      this.pendingSelectionText = searchTerm
      this.submitAfterSelectClose = true

      if (this.shouldCommitOpenSelectionManually() && this.commitOpenSelection(event.target)) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      this.ignoreNextSubmit = true
      return
    }

    if (!this.hasSelectedValue()) return

    event.preventDefault()
    event.stopPropagation()
    this.formElement?.requestSubmit()
  }

  handleRepeatableEnter(event) {
    if (!this.isSelectOpen()) {
      if (!this.hasSelectedValue()) return

      event.preventDefault()
      event.stopPropagation()
      this.formElement?.requestSubmit()
      return
    }

    const searchTerm = this.searchTermFor(event.target)
    if (this.isDuplicateCandidate(searchTerm)) {
      event.preventDefault()
      event.stopPropagation()
      this.syncDuplicateSearchFeedback(searchTerm)
      return
    }

    if (!this.shouldCommitOpenSelectionManually()) return

    const selectionText = this.highlightedOptionText() || searchTerm
    if (!this.selectOptionText(selectionText)) return

    event.preventDefault()
    event.stopPropagation()
    requestAnimationFrame(() => this.formElement?.requestSubmit())
  }

  handleEscape(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isSelectOpen()) {
      this.pendingSelectionText = null
      this.closeSelect()
      return
    }

    this.cancel()
  }

  onPointerDown(event) {
    if (!this.isExpanded()) return
    if (this.element.contains(event.target)) return
    if (this.isWithinOwnedSelectDropdown(event.target)) {
      this.captureLayoutAnchor()
      return
    }

    if (this.isSelectOpen()) {
      this.cancel()
      return
    }

    this.cancel()
  }

  onOpenRequest(event) {
    if (event.detail?.id === this.element.id) return
    if (!this.isExpanded()) return

    this.cancel()
  }

  onSelectOpen() {
    this.selectOpen = true
    this.captureLayoutAnchor()
    this.installForcedDropdownPosition()
    this.positionDropdownBelow()
    this.focusSearchField()
  }

  onSelectClose() {
    this.selectOpen = false
    this.clearDropdownDuplicateValidation()

    if (this.repeatableValue) {
      if (this.isExpanded()) this.focusSelectionElement()
      return
    }

    if (this.hasSelectedValue() || this.pendingSelectionText) {
      this.autoOpenCancelled = true
    }
    this.commitPendingSelection()

    if (this.submitAfterSelectClose) {
      this.submitAfterSelectClose = false
      this.ignoreNextSubmit = false
      if (this.hasSelectedValue()) {
        requestAnimationFrame(() => this.formElement?.requestSubmit())
      }
    }

    if (this.isExpanded()) {
      this.focusSelectionElement()
    }

    if (this.ignoreNextSubmit) {
      requestAnimationFrame(() => {
        this.ignoreNextSubmit = false
      })
    }
  }

  onFormSubmit(event) {
    if (this.hasDuplicateSelection()) {
      event.preventDefault()
      event.stopPropagation()
      this.ignoreNextSubmit = false
      this.submitAfterSelectClose = false
      this.reportDuplicateSelection()
      return
    }

    if (this.ignoreNextSubmit) {
      event.preventDefault()
      event.stopPropagation()
      this.ignoreNextSubmit = false
      return
    }

    const selectedValue = this.pendingSubmitSelectionValue || this.selectTarget.value
    if (this.creatingValue || !selectedValue) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    const confirmation = this.confirmationFor(selectedValue)
    if (confirmation && this.confirmedSelectionValue !== `${selectedValue}` && !window.confirm(confirmation)) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    this.confirmedSelectionValue = null
    this.pendingSubmitSelectionValue = null
    this.submittedValue = selectedValue
    this.submittedLabel = this.selectedPersonLabelFor(this.submittedValue)
    if (this.clearExpandedOnSubmitValue) this.setExpandedUrl(false)
    this.captureLayoutAnchor({ refresh: true })
    this.observeRepeatableInsertion()
    this.setCreatingState(true)
  }

  onTurboSubmitEnd(event) {
    if (event.detail?.success && this.repeatableValue) {
      this.repeatableSubmissionSucceeded = true
      this.completeObservedRepeatableSubmission()
      return
    }

    this.stopObservingRepeatableInsertion()
    this.layoutAnchorTop = null
    this.setCreatingState(false)
    if (event.detail?.success || event.detail?.fetchResponse?.statusCode !== 422) return

    this.handleUnprocessableSelection(this.selectTarget?.value)
  }

  submitSelection(event) {
    if (!this.repeatableValue || this.creatingValue) return

    const selectionData = event?.detail?.event?.params?.data
    const value = selectionData?.id || this.selectTarget?.value
    if (!value) return

    if (this.hasDuplicateSelection()) {
      this.reportDuplicateSelection()
      return
    }

    if (this.consumeRejectedSelection(value)) return

    const confirmation = this.confirmationFor(value, selectionData?.element)
    if (confirmation && this.confirmedSelectionValue !== `${value}` && !window.confirm(confirmation)) {
      this.rejectedSelectionValue = `${value}`
      return
    }

    this.rejectedSelectionValue = null
    this.confirmedSelectionValue = `${value}`
    this.pendingSubmitSelectionValue = value
    requestAnimationFrame(() => this.formElement?.requestSubmit())
  }

  onSelectChange() {
    if (this.hasSelectedValue()) this.pendingModalText = null

    const value = this.selectTarget?.value
    const rejectionConsumed = value && this.consumeRejectedSelection(value)
    if (this.repeatableValue && value && !rejectionConsumed && this.confirmedSelectionValue !== `${value}`) {
      const confirmation = this.confirmationFor(value)
      if (confirmation) {
        if (window.confirm(confirmation)) {
          this.confirmedSelectionValue = `${value}`
          this.rejectedSelectionValue = null
        } else {
          this.rejectedSelectionValue = `${value}`
        }
      }
    }

    if (this.hasDuplicateSelection()) {
      this.showDuplicateValidation(`Already added: ${this.selectedPersonLabelFor(this.selectTarget?.value)}`)
    } else {
      this.clearDuplicateValidation()
    }
  }

  onSearchInput(event) {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target !== this.ownedSearchField()) return

    this.pendingModalText = target.value.trim()
    this.syncDuplicateSearchFeedback(target.value)
  }

  submitSelected(event) {
    event?.preventDefault()
    if (this.creatingValue) return
    const value = this.selectTarget?.value
    if (!value) return
    this.create(value)
  }

  async create(value) {
    if (this.creatingValue) return

    this.setCreatingState(true)
    this.setExpandedUrl(true)
    document.dispatchEvent(new CustomEvent("canvas-activity:start", {
      bubbles: true,
      detail: { control: this.hasSubmitTarget ? this.submitTarget : document.activeElement }
    }))

    try {
      const body = this.buildRequestBody(value)
      document.dispatchEvent(new CustomEvent("canvas-activity:init", { bubbles: true }))
      const response = await post(this.urlValue, {
        body: JSON.stringify(body),
        contentType: "application/json",
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        this.dispatch("created", { detail: { value } })
        this.clearSelection()
      } else if (response.unprocessableEntity) {
        this.handleUnprocessableSelection(value)
      } else {
        this.setExpandedUrl(false)
        this.escalateToModal(value)
      }
    } catch (error) {
      console.error("Inline assigned job create error:", error)
      this.setExpandedUrl(false)
      this.escalateToModal(value)
    } finally {
      this.setCreatingState(false)
      document.dispatchEvent(new CustomEvent("canvas-activity:complete", { bubbles: true }))
      document.dispatchEvent(new CustomEvent("canvas-activity:visible", { bubbles: true }))
    }
  }

  handleUnprocessableSelection(value) {
    this.setExpandedUrl(false)

    if (this.hasDuplicateSelection()) {
      this.showDuplicateValidation(`Already added: ${this.selectedPersonLabelFor(value)}`)
    } else {
      this.escalateToModal(value)
    }
  }

  setCreatingState(creating) {
    this.creatingValue = creating
    this.element.classList.toggle("opacity-50", creating)

    if (this.hasSelectShellTarget) {
      this.selectShellTarget.classList.toggle("pointer-events-none", creating)
      this.selectShellTarget.setAttribute("aria-busy", creating.toString())
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = creating
      this.submitTarget.setAttribute("aria-busy", creating.toString())
    }
    if (this.hasSubmitIconTarget) {
      this.submitIconTarget.classList.toggle("hidden", creating)
    }
    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.toggle("hidden", !creating)
    }
  }

  completeRepeatableSubmission() {
    const value = this.submittedValue
    const label = this.submittedLabel
    const searchField = this.ownedSearchField()

    if (searchField) {
      searchField.value = ""
    }

    if (label && !this.isDuplicateCandidate(label)) {
      this.existingLabelsValue = [...this.existingLabelsValue, label]
    }

    this.clearSelection()
    this.removeSubmittedOption(value, label)
    this.submittedValue = null
    this.submittedLabel = null

    if (!this.isExpanded()) {
      this.layoutAnchorTop = null
      this.setCreatingState(false)
      return
    }

    this.refreshOpenResults(() => {
      this.removeVisibleExistingOptions()
      this.positionDropdownBelow()
      this.restoreLayoutAnchor(() => this.setCreatingState(false))
    })
  }

  observeRepeatableInsertion() {
    if (!this.repeatableValue || !this.element.parentElement) return

    this.stopObservingRepeatableInsertion()
    this.repeatableInsertionObserved = false
    this.repeatableSubmissionSucceeded = false
    this.repeatableInsertionObserver = new MutationObserver((mutations) => {
      const insertedPerson = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
        return node instanceof HTMLElement && node.matches("[data-assigned-person-id]")
      }))
      if (!insertedPerson) return

      this.repeatableInsertionObserved = true
      this.completeObservedRepeatableSubmission()
    })
    this.repeatableInsertionObserver.observe(this.element.parentElement, { childList: true })
  }

  completeObservedRepeatableSubmission() {
    if (!this.repeatableInsertionObserved || !this.repeatableSubmissionSucceeded) return

    this.stopObservingRepeatableInsertion()
    requestAnimationFrame(() => this.completeRepeatableSubmission())
  }

  stopObservingRepeatableInsertion() {
    this.repeatableInsertionObserver?.disconnect()
    this.repeatableInsertionObserver = null
    this.repeatableInsertionObserved = false
    this.repeatableSubmissionSucceeded = false
  }

  removeSubmittedOption(value, label) {
    if (!value || !this.hasSelectTarget) return

    const normalizedLabel = this.normalizeLabel(label)
    const matchedOptions = Array.from(this.selectTarget.options).filter((candidate) => {
      if (candidate.value === `${value}`) return true

      const candidateLabel = candidate.dataset.inlineAssignedJobPersonLabel
      return normalizedLabel && this.normalizeLabel(candidateLabel) === normalizedLabel
    })
    const groups = new Set(matchedOptions.map((option) => option.parentElement))

    matchedOptions.forEach((option) => option.remove())
    groups.forEach((group) => {
      if (group instanceof HTMLOptGroupElement && group.children.length === 0) group.remove()
    })
  }

  removeVisibleExistingOptions() {
    const normalizedExistingLabels = new Set(this.existingLabelsValue.map((label) => this.normalizeLabel(label)).filter(Boolean))
    if (normalizedExistingLabels.size === 0) return

    const groups = new Set()
    Array.from(this.ownedOpenDropdown()?.querySelectorAll(".select2-results__option[role='option']") || []).forEach((option) => {
      const optionLabel = option.querySelector("[data-select2-option-label]")?.dataset.select2OptionLabel
      const text = (option.textContent || "").replace(/^\s*✨\s*/, "").replace(/^Create\s+/i, "")
      const normalizedLabel = this.normalizeLabel(optionLabel || text)
      if (!normalizedExistingLabels.has(normalizedLabel)) return

      groups.add(option.closest(".select2-results__options--nested"))
      option.remove()
    })

    groups.forEach((group) => {
      if (group && group.querySelector(".select2-results__option[role='option']") === null) {
        group.closest(".select2-results__option[role='group']")?.remove()
      }
    })
  }

  refreshOpenResults(onRefreshed) {
    if (!this.isSelectOpen() || !this.hasSelectTarget) {
      onRefreshed()
      return
    }

    const instance = $(this.selectTarget).data("select2")
    if (!instance) {
      onRefreshed()
      return
    }

    const handleResults = () => {
      // Select2's Observable exposes on/trigger but no off, so remove this one-shot listener directly.
      const listeners = instance.listeners?.["results:all"] || []
      const listenerIndex = listeners.indexOf(handleResults)
      if (listenerIndex >= 0) listeners.splice(listenerIndex, 1)
      onRefreshed()
    }
    instance.on("results:all", handleResults)
    instance.trigger("query", { term: this.ownedSearchField()?.value || "" })
  }

  captureLayoutAnchor({ refresh = false } = {}) {
    if (!this.repeatableValue || (!refresh && this.layoutAnchorTop != null)) return

    this.layoutAnchorTop = this.element.getBoundingClientRect().top
  }

  restoreLayoutAnchor(onRestored = null) {
    const layoutAnchorTop = this.layoutAnchorTop
    this.layoutAnchorTop = null

    if (!this.repeatableValue || layoutAnchorTop == null) {
      onRestored?.()
      return
    }

    if (!this.element.isConnected) {
      onRestored?.()
      return
    }

    const delta = this.element.getBoundingClientRect().top - layoutAnchorTop
    if (Math.abs(delta) > 0.5) window.scrollBy(0, delta)
    this.positionDropdownBelow()
    onRestored?.()
  }

  installForcedDropdownPosition() {
    if (!this.forceDropdownBelowValue || !this.hasSelectTarget) return

    const dropdown = $(this.selectTarget).data("select2")?.dropdown
    if (!dropdown?._positionDropdown || dropdown.inlineAssignedJobPositionInstalled) return

    const originalPositionDropdown = dropdown._positionDropdown.bind(dropdown)
    dropdown._positionDropdown = (...args) => {
      originalPositionDropdown(...args)
      this.positionDropdownBelow(dropdown)
    }
    dropdown.inlineAssignedJobPositionInstalled = true
  }

  positionDropdownBelow(dropdown = null) {
    if (!this.forceDropdownBelowValue || !this.hasSelectTarget) return

    const positionedDropdown = dropdown || $(this.selectTarget).data("select2")?.dropdown
    if (!positionedDropdown?.$dropdownContainer || !positionedDropdown?.$container) return

    const containerOffset = positionedDropdown.$container.offset()
    if (!containerOffset) return

    let offsetParent = positionedDropdown.$dropdownParent
    if (offsetParent?.css("position") === "static") {
      offsetParent = offsetParent.offsetParent()
    }
    const parentOffset = offsetParent?.offset() || { top: 0, left: 0 }

    positionedDropdown.$dropdown
      .removeClass("select2-dropdown--above")
      .addClass("select2-dropdown--below")
    positionedDropdown.$container
      .removeClass("select2-container--above")
      .addClass("select2-container--below")
    positionedDropdown.$dropdownContainer.css({
      left: containerOffset.left - parentOffset.left,
      top: containerOffset.top + positionedDropdown.$container.outerHeight(false) - parentOffset.top
    })
  }

  buildRequestBody(value) {
    const params = { [this.fieldValue]: value ?? "" }
    if (this.hasLabelFieldValue && this.labelFieldValue) {
      params[this.labelFieldValue] = this.selectedLabelFor(value)
    }
    const body = this.modelValue ? { [this.modelValue]: params } : params
    body.refresh_mode = "current"
    return body
  }

  escalateToModal(value) {
    this.openModal(value)
  }

  openModal(value) {
    if (!this.fallbackUrlValue) return

    const url = new URL(this.fallbackUrlValue, window.location.origin)
    if (value) {
      const param = this.hasModalParamValue ? this.modalParamValue : `${this.modelValue}[${this.fieldValue}]`
      url.searchParams.set(param, value)
    }
    url.searchParams.set("return_to", window.location.href)

    const link = document.createElement("a")
    link.href = url.toString()
    link.dataset.turboFrame = "modal"
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  pendingModalValue() {
    const selectedValue = this.selectTarget?.value
    if (selectedValue) return selectedValue

    return this.ownedSearchField()?.value?.trim() || this.pendingModalText || ""
  }

  clearSelection() {
    if (!this.hasSelectTarget) return
    this.confirmedSelectionValue = null
    this.rejectedSelectionValue = null
    this.pendingSubmitSelectionValue = null
    this.pendingSelectionText = null
    this.pendingModalText = null
    this.submitAfterSelectClose = false
    this.clearDuplicateValidation()
    $(this.selectTarget).val(null).trigger("change.select2")
  }

  confirmationFor(value, selectedOption = null) {
    const option = selectedOption || Array.from(this.selectTarget.options).find((candidate) => candidate.value === `${value}`)
    return this.confirmationsValue[`${value}`] || option?.dataset?.planningContextConfirmation
  }

  consumeRejectedSelection(value) {
    if (this.rejectedSelectionValue !== `${value}`) return false

    this.rejectedSelectionValue = null
    return true
  }

  closeSelect() {
    this.autoOpenCancelled = true
    const controller = this.selectController()
    if (!controller?.hasActiveSelect2Instance?.()) return

    controller.pluginMainEl = controller.pluginMainEl || this.selectTarget
    $(this.selectTarget).select2("close")
    this.selectOpen = false
  }

  onTurboRender() {
    // A collapsed control already matches the server-rendered state. Replaying
    // collapse on every Turbo render clears every Select2-backed field and is
    // especially expensive on large canvases in WebKit.
    if (!this.expandedStateDrifted()) return

    ;[0, 25, 100, 250].forEach((delay) => {
      setTimeout(() => this.restoreExpandedState(), delay)
    })
  }

  observeVisibility() {
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== "attributes" || mutation.attributeName !== "class") return

        if (this.expandedStateDrifted()) {
          this.scheduleRestore()
        }
      })
    })

    if (this.hasTriggerTarget) {
      this.visibilityObserver.observe(this.triggerTarget, { attributes: true, attributeFilter: ["class"] })
    }

    if (this.hasInputWrapperTarget) {
      this.visibilityObserver.observe(this.inputWrapperTarget, { attributes: true, attributeFilter: ["class"] })
    }
  }

  prepareSelectForEditing(attempt = 0) {
    if (!this.hasSelectTarget) return
    if (attempt === 0 && this.selectInitScheduled) return

    this.selectInitScheduled = true
    setTimeout(() => {
      if (this.autoOpenCancelled || !this.isExpanded()) {
        this.selectInitScheduled = false
        return
      }

      const controller = this.selectController()
      if (!controller) {
        this.selectInitScheduled = false
        if (attempt < 4 && this.isExpanded()) this.prepareSelectForEditing(attempt + 1)
        return
      }

      // Late expansion restores can run after typing starts. Repair missing
      // markup without destroying the active picker and its search/highlight.
      controller.ensurePluginInstance()
      this.openSelect()

      setTimeout(() => {
        this.selectInitScheduled = false
        if (!this.element.querySelector(".select2-selection") && attempt < 4 && this.isExpanded()) {
          this.prepareSelectForEditing(attempt + 1)
        }
      }, 100)
    }, attempt === 0 ? 50 : 100)
  }

  openSelect() {
    const controller = this.selectController()
    if (!controller) return

    ;[0, 25, 75, 150].forEach((delay) => {
      setTimeout(() => {
        if (this.autoOpenCancelled || !this.isExpanded() || this.hasSelectedValue()) return
        controller.open()
        this.focusSearchField()
      }, delay)
    })
  }

  focusSelectionElement() {
    ;[0, 50, 150, 300, 600, 1000].forEach((delay) => {
      setTimeout(() => {
        const selection = this.element.querySelector(".select2-selection")
        if (!selection) return

        if (!selection.hasAttribute("tabindex")) {
          selection.setAttribute("tabindex", "0")
        }

        selection.focus()
      }, delay)
    })
  }

  focusSearchField() {
    ;[0, 25, 75, 150, 300].forEach((delay) => {
      setTimeout(() => {
        this.ownedSearchField()?.focus()
      }, delay)
    })
  }

  selectController() {
    if (!this.hasSelectTarget) return null

    const identifiers = ["fields--job-super-select", "fields--super-select"]
    for (const identifier of identifiers) {
      const selectWrapper = this.selectTarget.closest(`[data-controller~="${identifier}"]`)
      if (!selectWrapper) continue

      const controller = this.application.getControllerForElementAndIdentifier(selectWrapper, identifier)
      if (controller) return controller
    }

    return null
  }

  persistExpandedUrlIfVisible() {
    if (!this.isExpanded()) return
    const currentExpandedId = new URL(window.location.href).searchParams.get("expand")
    if (currentExpandedId && currentExpandedId !== this.element.id) return
    if (!this.shouldAutoExpand()) {
      this.renderCollapsedState()
      return
    }
    this.setExpandedUrl(true)
  }

  syncReturnToField() {
    if (!this.hasReturnToTarget) return
    this.returnToTarget.value = window.location.href
  }

  get formElement() {
    return this.element.querySelector("form")
  }

  isExpanded() {
    if (!this.hasInputWrapperTarget) return false
    return !this.inputWrapperTarget.hidden && !this.inputWrapperTarget.classList.contains("hidden")
  }

  isSelectOpen() {
    return this.selectOpen || this.ownedOpenDropdown() !== null
  }

  hasSelectedValue() {
    return Boolean(this.selectTarget?.value)
  }

  selectedLabelFor(value) {
    if (!this.hasSelectTarget) return value ?? ""

    const selectedOption = Array.from(this.selectTarget.options).find((option) => option.value === `${value ?? ""}`)
    return selectedOption?.text?.trim() || `${value ?? ""}`
  }

  selectedPersonLabelFor(value) {
    if (!this.hasSelectTarget) return value ?? ""

    const selectedOption = Array.from(this.selectTarget.options).find((option) => option.value === `${value ?? ""}`)
    return selectedOption?.dataset?.inlineAssignedJobPersonLabel?.trim() || selectedOption?.text?.trim() || `${value ?? ""}`
  }

  hasDuplicateSelection() {
    const value = this.selectTarget?.value
    if (!value) return false

    return this.isDuplicateCandidate(value) || this.isDuplicateCandidate(this.selectedPersonLabelFor(value))
  }

  reportDuplicateSelection() {
    this.showDuplicateValidation()
  }

  showDuplicateValidation(message = "Already added.", { focus = true } = {}) {
    const selection = this.element.querySelector(".select2-selection")
    const messageElement = this.inlineDuplicateMessageElement()

    selection?.setAttribute("aria-invalid", "true")
    selection?.setAttribute("aria-describedby", messageElement.id)
    messageElement.textContent = message
    messageElement.hidden = false

    if (focus && selection instanceof HTMLElement) {
      selection.focus()
    }
  }

  clearDuplicateValidation() {
    this.clearInlineDuplicateValidation()
    this.clearDropdownDuplicateValidation()
  }

  clearInlineDuplicateValidation() {
    const selection = this.element.querySelector(".select2-selection")

    selection?.removeAttribute("aria-invalid")
    selection?.removeAttribute("aria-describedby")
    if (this.inlineDuplicateMessage?.isConnected) {
      this.inlineDuplicateMessage.hidden = true
    }
  }

  normalizeLabel(label) {
    return `${label ?? ""}`.trim().replace(/\s+/g, " ").toLowerCase()
  }

  isDuplicateCandidate(candidate) {
    const normalizedCandidate = this.normalizeLabel(candidate)
    if (!normalizedCandidate) return false

    return this.existingLabelsValue.some((label) => this.normalizeLabel(label) === normalizedCandidate)
  }

  syncDuplicateSearchFeedback(value) {
    const term = value?.trim() || ""
    if (this.isDuplicateCandidate(term)) {
      this.clearInlineDuplicateValidation()
      this.showDropdownDuplicateValidation(term)
    } else if (!this.hasDuplicateSelection()) {
      this.clearDuplicateValidation()
    } else {
      this.clearDropdownDuplicateValidation()
    }
  }

  inlineDuplicateMessageElement() {
    if (this.inlineDuplicateMessage?.isConnected) return this.inlineDuplicateMessage

    const message = document.createElement("div")
    message.id = `${this.element.id || "inline-select-create"}-duplicate-message`
    message.className = "inline-assigned-job-duplicate-message"
    message.hidden = true

    const row = this.formElement?.querySelector(".flex.items-end")
    if (row) {
      row.insertAdjacentElement("afterend", message)
    } else if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.appendChild(message)
    }

    this.inlineDuplicateMessage = message
    return message
  }

  showDropdownDuplicateValidation(term) {
    this.replaceDuplicateCreateOption(term)
    requestAnimationFrame(() => {
      if (this.normalizeLabel(this.ownedSearchField()?.value) === this.normalizeLabel(term)) {
        this.replaceDuplicateCreateOption(term)
      }
    })
  }

  replaceDuplicateCreateOption(term) {
    const dropdown = this.ownedOpenDropdown()
    if (!dropdown) return

    dropdown.classList.add("inline-assigned-job-dropdown--duplicate")
    const option = this.duplicateCreateOption(dropdown, term)
    if (!option) return

    if (!option.dataset.inlineAssignedJobOriginalText) {
      option.dataset.inlineAssignedJobOriginalText = option.textContent?.trim() || ""
    }

    option.classList.add("inline-assigned-job-duplicate-option")
    option.setAttribute("aria-disabled", "true")
    option.replaceChildren(this.duplicateCreateOptionContent(term))
  }

  clearDropdownDuplicateValidation() {
    const dropdown = this.ownedOpenDropdown()
    if (!dropdown) return

    dropdown.classList.remove("inline-assigned-job-dropdown--duplicate")
    dropdown.querySelectorAll(".inline-assigned-job-duplicate-option").forEach((option) => {
      if (option.dataset.inlineAssignedJobOriginalText) {
        option.textContent = option.dataset.inlineAssignedJobOriginalText
      }

      option.classList.remove("inline-assigned-job-duplicate-option")
      option.removeAttribute("aria-disabled")
      delete option.dataset.inlineAssignedJobOriginalText
    })
  }

  duplicateCreateOption(dropdown, term) {
    const normalizedTerm = this.normalizeLabel(term)
    const options = Array.from(dropdown.querySelectorAll(".select2-results__option[role='option']"))
    const createOption = options.find((option) => {
      const text = option.dataset.inlineAssignedJobOriginalText || option.textContent || ""
      return this.normalizeLabel(text.replace(/^\s*✨\s*/, "").replace(/^Create\s+/i, "")) === normalizedTerm
    })

    return createOption || dropdown.querySelector(".select2-results__option--highlighted")
  }

  duplicateCreateOptionContent(term) {
    const wrapper = document.createElement("span")
    wrapper.className = "inline-assigned-job-duplicate-option-content"

    const iconBadge = document.createElement("span")
    iconBadge.className = "inline-assigned-job-duplicate-option-icon-badge"

    const icon = document.createElement("i")
    icon.className = "fal fa-circle-exclamation"
    icon.setAttribute("aria-hidden", "true")
    iconBadge.append(icon)

    const label = document.createElement("span")
    label.className = "inline-assigned-job-duplicate-option-label"

    const prefix = document.createElement("span")
    prefix.className = "inline-assigned-job-duplicate-option-prefix"
    prefix.textContent = "Already added:"

    const name = document.createElement("span")
    name.className = "inline-assigned-job-duplicate-option-name"
    name.textContent = term

    label.append(prefix, document.createTextNode(" "), name)

    wrapper.append(iconBadge, label)
    return wrapper
  }

  bindSelectChange() {
    if (!this.hasSelectTarget || this.selectChangeElement === this.selectTarget) return

    this.unbindSelectChange()
    this.selectTarget.addEventListener("change", this.handleSelectChange)
    this.selectChangeElement = this.selectTarget
  }

  unbindSelectChange() {
    this.selectChangeElement?.removeEventListener("change", this.handleSelectChange)
    this.selectChangeElement = null
  }

  searchTermFor(target) {
    if (target instanceof HTMLInputElement && target.matches(".select2-search__field")) {
      return target.value?.trim() || ""
    }

    return this.ownedSearchField()?.value?.trim() || ""
  }

  commitOpenSelection(target) {
    const selectionText = this.highlightedOptionText() || this.searchTermFor(target)
    if (!this.selectOptionText(selectionText)) return false

    this.pendingSelectionText = null
    this.closeSelect()
    return true
  }

  shouldCommitOpenSelectionManually() {
    return this.highlightedOptionText() === ""
  }

  commitPendingSelection() {
    const pendingText = this.pendingSelectionText?.trim()
    this.pendingSelectionText = null

    if (this.hasSelectedValue()) return
    this.selectOptionText(pendingText)
  }

  selectOptionText(text) {
    const selectionText = text?.trim()
    if (!selectionText || !this.hasSelectTarget) return false

    const existingOption = Array.from(this.selectTarget.options).find((option) => {
      return option.value === selectionText || option.text === selectionText
    })

    if (existingOption) {
      existingOption.selected = true
      this.clearDuplicateValidation()
      $(this.selectTarget).val(existingOption.value).trigger("change.select2").trigger("change")
      return true
    }

    this.selectTarget.add(new Option(selectionText, selectionText, true, true))
    this.clearDuplicateValidation()
    $(this.selectTarget).val(selectionText).trigger("change.select2").trigger("change")
    return true
  }

  highlightedOptionText() {
    const highlightedOption = this.ownedOpenDropdown()?.querySelector(".select2-results__option--highlighted")
    return highlightedOption?.textContent?.trim() || ""
  }

  isWithinSelectControl(target) {
    if (!(target instanceof Element)) return false
    return target.closest(".select2-container") !== null || target.matches(".select2-search__field")
  }

  isWithinOwnedControl(target) {
    if (!(target instanceof Element)) return false
    return this.element.contains(target) || this.isWithinOwnedSelectDropdown(target)
  }

  isWithinOwnedSelectDropdown(target) {
    if (!(target instanceof Element)) return false

    const dropdown = this.ownedOpenDropdown()
    return dropdown ? dropdown.contains(target) : false
  }

  ownedSelectResultsId() {
    const rendered = this.element.querySelector(".select2-selection__rendered")
    if (!rendered?.id) return null

    return rendered.id.replace(/-container$/, "-results")
  }

  ownedOpenDropdown() {
    const resultsId = this.ownedSelectResultsId()
    if (!resultsId) return null

    const results = document.getElementById(resultsId)
    const dropdown = results?.closest(".select2-dropdown")
    if (!dropdown) return null

    return dropdown.closest(".select2-container--open") ? dropdown : null
  }

  ownedSearchField() {
    return this.ownedOpenDropdown()?.querySelector(".select2-search__field") || null
  }
}
